import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { isExpired, decodeToken } from 'react-jwt';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const httpClient = axios.create({
  baseURL: import.meta.env.VITE_AUTH_API_BASE_URL,
});

httpClient.interceptors.request.use((config) => {
  const activeDomain =
    localStorage.getItem('activeDomain') ||
    (() => {
      const hostParts = window.location.hostname.split('.');
      if (hostParts.length > 2 && hostParts[0] !== 'www' && hostParts[0] !== 'app') {
        return hostParts[0];
      }
      return undefined;
    })();
  if (activeDomain) {
    config.headers['x-domain-id'] = activeDomain;
  }
  return config;
});

httpClient.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    const { code, message, response = {} } = error;
    const { data = {}, status } = response;
    const { errorCode, message: errorMessage } = data;
    const fallBackMEssage = errorCode ? `API error code: ${errorCode}` : `Http error code: ${code}`;
    return Promise.reject({
      httpCode: code,
      message: errorMessage || message || fallBackMEssage,
      errorCode,
      status,
    });
  },
);

interface AccessToken {
  accessToken?: string;
  expiryTime?: number;
}

interface IUserContext {
  isLoading?: boolean;
  user?: any;
  serverError?: any;
  isSuperAdmin: boolean;
  signUp: (signupRequest: any) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: (credential: string) => Promise<void>;
  signout: () => Promise<void>;
  getAccessToken: () => Promise<string | undefined>;
  hasPermission: (permission: string, access?: any) => boolean;
  hasRole: (role: string) => boolean;
  switchTenant?: (tenantId: string) => void;
}

interface AppUser {
  id: string;
  identifier: string;
  tenantId: string;
  domainId?: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string[];
  licenseData?: any;
  createdDate?: string;
}

const UserContext = React.createContext<IUserContext>({} as any);

const UserContextProvider = ({ children }: any) => {
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const location = useLocation();

  const [appUser, setAppUser] = useState<AppUser | null>(null);

  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<AccessToken>({});
  const [serverError, setServerError] = useState();

  useEffect(() => {
    const checkLogin = async () => {
      setLoading(true);
      const tokenFromQuery = queryParams.get('token');
      const domainFromQuery = queryParams.get('domain');
      if (domainFromQuery) {
        localStorage.setItem('activeDomain', domainFromQuery);
        window.dispatchEvent(new CustomEvent('minotii-domain-changed', { detail: { domainId: domainFromQuery } }));
      }
      const username = queryParams.get('username') || '';
      const password = queryParams.get('password') || '';
      if (tokenFromQuery && !isExpired(tokenFromQuery)) {
        try {
          const accessTokens = parseAccessToken(tokenFromQuery);
          localStorage.removeItem('access-token-storage');
          localStorage.setItem('access-token-storage', JSON.stringify(accessTokens));
          const targetDomain = domainFromQuery || localStorage.getItem('activeDomain') || accessTokens?.domainId || 'general';
          if (accessTokens?.domainId !== targetDomain && accessTokens?.tenantId) {
            await refreshToken(accessTokens.tenantId);
          }
          queryParams.delete('token');
          if (domainFromQuery) queryParams.delete('domain');
          const remainingQuery = queryParams.toString();
          navigate(location.pathname + (remainingQuery ? `?${remainingQuery}` : ''), { replace: true });
        } catch (error: any) {
          console.error('Failed to parse token from URL', error);
          setAccessToken({});
          setAppUser(null);
        }
      } else if (username && password) {
        try {
          await signIn(username, password);
          navigate(location.pathname);
        } catch (error: any) {
          setAccessToken({});
          setAppUser(null);
        }
      } else {
        const storedTokens = localStorage.getItem('access-token-storage');
        if (storedTokens) {
          try {
            const accessTokens = JSON.parse(storedTokens);
            const targetDomain = localStorage.getItem('activeDomain') || accessTokens?.domainId || 'general';
            if (!isExpired(accessTokens.accessToken)) {
              if (accessTokens.domainId && accessTokens.domainId !== targetDomain && accessTokens.tenantId) {
                await refreshToken(accessTokens.tenantId);
              } else {
                parseAccessToken(accessTokens.accessToken);
              }
            } else {
              await refreshToken(accessTokens.tenantId);
            }
          } catch (error: any) {
            console.log(error);
            setAccessToken({});
            setAppUser(null);
          }
        } else {
          setAccessToken({});
          setAppUser(null);
        }
      }
      setLoading(false);
    };
    checkLogin();
  }, []);

  useEffect(() => {}, []);

  const signUp = async (signupRequest: any) => {
    setLoading(true);
    try {
      await httpClient.post(`/api/signup`, signupRequest);
    } catch (error: any) {
      setServerError(error);
      setAppUser(null);
      throw error;
    }
    setLoading(false);
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await httpClient.post(
        `/api/signin`,
        { email, password },
        {
          withCredentials: true,
        },
      );
      const accessTokens = parseAccessToken(response.data);
      localStorage.removeItem('access-token-storage');
      localStorage.setItem('access-token-storage', JSON.stringify(accessTokens));
    } catch (error: any) {
      setServerError(error);
      setAppUser(null);
      throw error;
    }
    setLoading(false);
  };

  const signInWithGoogle = async (credential: string) => {
    setLoading(true);
    try {
      const response = await httpClient.post(
        `/api/google-signin`,
        { credential },
        {
          withCredentials: true,
        },
      );
      const accessTokens = parseAccessToken(response.data);
      localStorage.removeItem('access-token-storage');
      localStorage.setItem('access-token-storage', JSON.stringify(accessTokens));
    } catch (error: any) {
      setServerError(error);
      setAppUser(null);
      throw error;
    }
    setLoading(false);
  };

  const signout = async () => {
    setLoading(true);
    try {
      localStorage.removeItem('access-token-storage');
      setAccessToken({});
      setAppUser(null);
      navigate('/');
    } catch (error: any) {
      setServerError(error);
      setAppUser(null);
      throw error;
    }
    setLoading(false);
  };

  const getAccessToken = async () => {
    if (appUser && accessToken.accessToken) {
      const now = Math.round(Date.now() / 1000);
      if (accessToken.expiryTime && accessToken.expiryTime - now < 60) {
        try {
          const accessToken = await refreshToken(appUser.tenantId);
          return accessToken;
        } catch (error) {
          console.error(error);
          signout();
          throw error;
        }
      } else {
        return accessToken.accessToken;
      }
    }
    return '';
  };

  const refreshToken = async (tenantId: string) => {
    setLoading(true);
    try {
      const response = await httpClient.get(`/api/tenants/${tenantId}/token/refresh`, {
        headers: { Authorization: `Bearer ${accessToken.accessToken}` },
        withCredentials: true,
      });
      const tokens = parseAccessToken(response.data);
      localStorage.removeItem('access-token-storage');
      localStorage.setItem('access-token-storage', JSON.stringify(tokens));
      return tokens.accessToken;
    } catch (error: any) {
      console.log(error);
      setServerError(error);
      setAppUser(null);
      localStorage.removeItem('access-token-storage');
    }
    setLoading(false);
  };

  const parseAccessToken = (accessToken: string) => {
    const payload: any = decodeToken(accessToken as string);
    if (payload) {
      setAppUser({
        id: payload.id,
        identifier: payload.identifier,
        tenantId: payload.tenantId,
        domainId: payload.domainId,
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        roles: payload.roles || [],
        licenseData: payload.licenseData,
        createdDate: payload.createdDate,
      });
      setAccessToken({ accessToken, expiryTime: payload.exp });
      return {
        accessToken,
        expiryTime: payload.exp,
        tenantId: payload.tenantId,
        domainId: payload.domainId,
        licenseData: payload.licenseData,
      };
    } else {
      return {};
    }
  };

  const isSuperAdmin = Boolean(
    appUser?.roles &&
    (appUser.roles.includes('superadmin') || appUser.roles.includes('super_admin'))
  );

  const switchTenant = (tenantId: string) => {
    if (appUser && isSuperAdmin) {
      setAppUser({ ...appUser, tenantId });
    }
  };

  const hasPermission = (permission: string, access?: any): boolean => {
    // 0. Super-admin has universal platform access
    if (isSuperAdmin) {
      return true;
    }

    // Tenant Administrator check
    if (appUser?.roles?.includes('administrator')) {
      return true;
    }

    // 1. Entity Owner check (an owner has full access on their entity)
    if (access && access.role === 'owner') {
      return true;
    }

    // 2. License Check First (Master Gate)
    // We assume any permission with a colon is a license-gated feature.
    const licensePermissions = appUser?.licenseData?.permissions || [];
    const hasLicense = licensePermissions.some((p: any) => p.fqn === permission);

    if (permission.includes(':') && !hasLicense) {
      return false;
    }

    // 3. Entity Access Control Check (ACL)
    if (access) {
      return access.permissions?.includes(permission) || false;
    }

    // 4. Final Fallback (for non-entity permissions, or if no access provided)
    return hasLicense || !permission.includes(':');
  };

  const hasRole = (role: string): boolean => {
    if (!appUser || !appUser.roles) {
      return false;
    }
    return appUser.roles.includes(role);
  };

  const userContext = {
    isLoading: loading,
    user: appUser,
    serverError,
    isSuperAdmin,
    signUp,
    signIn,
    signInWithGoogle,
    signout,
    getAccessToken,
    hasPermission,
    hasRole,
    switchTenant,
  };

  return <UserContext.Provider value={userContext}>{children}</UserContext.Provider>;
};

function useUserContext() {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within a AuthContextProvider');
  }
  return context;
}

export { UserContextProvider, useUserContext };
